import { useCallback, useEffect, useMemo, useReducer, type ReactNode } from "react";
import * as accountsApi from "../api/accountsApi";
import { listenForEndedSession, rememberCsrfToken } from "../api/http/sessionCredentials";
import { reportUiError, toUiError } from "../errors/uiError";
import { SessionContext, type SessionContextValue } from "./sessionContext";
import { runSignOutCleanups } from "./signOutCleanups";
import { csrfTokenOf, INITIAL_SESSION_STATE, transitionSession } from "./sessionMachine";

export default function SessionProvider({ children }: { children: ReactNode }) {
  const [session, dispatch] = useReducer(transitionSession, INITIAL_SESSION_STATE);

  useEffect(() => {
    if (session.status !== "checking") {
      return;
    }
    const controller = new AbortController();
    accountsApi.readCurrentSession(controller.signal).then(
      (current) => {
        if (controller.signal.aborted) {
          return;
        }
        dispatch(current === null ? { type: "noSessionFound" } : { type: "sessionFound", session: current });
      },
      (caught: unknown) => {
        if (controller.signal.aborted) {
          return;
        }
        const error = toUiError(caught);
        reportUiError(error);
        dispatch({ type: "sessionCheckFailed", error });
      },
    );
    return () => {
      controller.abort();
    };
  }, [session.status]);

  // The HTTP client sends the CSRF token of the signed-in session, and none otherwise.
  const csrfToken = csrfTokenOf(session);
  useEffect(() => {
    rememberCsrfToken(csrfToken);
  }, [csrfToken]);

  useEffect(() => {
    listenForEndedSession(() => {
      dispatch({ type: "sessionEnded" });
    });
    return () => {
      listenForEndedSession(null);
    };
  }, []);

  const signIn = useCallback(async (credentials: accountsApi.SignInCredentials) => {
    const started = await accountsApi.startSession(credentials);
    dispatch({ type: "signedIn", session: started });
  }, []);

  const signedInUserId = session.status === "signedIn" ? session.session.account.userId : null;
  const signOut = useCallback(async () => {
    await accountsApi.endCurrentSession();
    // What features kept in this browser for this account goes with it (W-06).
    if (signedInUserId !== null) runSignOutCleanups(signedInUserId);
    dispatch({ type: "signedOut" });
  }, [signedInUserId]);

  const retrySessionCheck = useCallback(() => {
    dispatch({ type: "retryRequested" });
  }, []);

  const value = useMemo<SessionContextValue>(
    () => ({ session, signIn, signOut, retrySessionCheck }),
    [session, signIn, signOut, retrySessionCheck],
  );

  return <SessionContext.Provider value={value}>{children}</SessionContext.Provider>;
}
